import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import {
    IRCAComplianceByMill,
    IRCAAPComplianceEntity,
    IActionPlanComplianceByMill,
    IRCAComplianceByResponsible,
    IAPComplianceByResponsible,
    IAPAvgPerUser,
    IRcaMethodologyUsedByUser,
    IArgsFilters,
    IRCADisplayNumber,
} from "./RCAAPComplianceEntityInterfaces";
import {
    getAPComplianceByResponse,
    getActionPlanComplianceByMill,
    getAvgActionPlansPerUserResponse,
    getRCAComplianceByMill,
    getRCAComplianceByResponsible,
    getRcaMethodologyUsedByUserResponse,
    getRCADisplayNumbers,
} from "api/queries/RCA/RCAComplianceAPI";
import { buildComplianceArgs, ComplianceArgsDateKey } from "./helpers";

export const RCAAPComplianceEntity: IRCAAPComplianceEntity = {
    GetRCAComplianceByMill: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRCAComplianceByMill, {
            fetchPolicy: "no-cache",
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.INPUT_DATE),
            },
        });

        const [rcaComplianceByMill, setRcaComplianceByMill] = useState<IRCAComplianceByMill[]>([]);

        useEffect(() => {
            if (data?.rca_compliance_by_mill && Array.isArray(data.rca_compliance_by_mill)) {
                setRcaComplianceByMill(data.rca_compliance_by_mill);
            }
        }, [data]);

        return {
            loading,
            rcaComplianceByMill,
            apolloQueryError: error,
        };
    },
    GetRCAComplianceByResponsible: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRCAComplianceByResponsible, {
            fetchPolicy: "no-cache",
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.INPUT_DATE),
            },
        });

        const [rcaComplianceByResponsible, setRcaComplianceByResponsible] = useState<IRCAComplianceByResponsible[]>([]);

        useEffect(() => {
            if (
                data?.rca_compliance_totals_by_responsible &&
                Array.isArray(data.rca_compliance_totals_by_responsible)
            ) {
                setRcaComplianceByResponsible(data.rca_compliance_totals_by_responsible);
            }
        }, [data]);

        return {
            loading,
            rcaComplianceByResponsible,
            apolloQueryError: error,
        };
    },
    GetActionPlanComplianceByMill: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getActionPlanComplianceByMill, {
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.INPUT_DATE),
            },
        });

        const [actionPlanComplianceByMill, setActionPlanComplianceByMill] = useState<IActionPlanComplianceByMill[]>([]);

        useEffect(() => {
            if (data?.action_plan_compliance_by_mill && Array.isArray(data.action_plan_compliance_by_mill)) {
                setActionPlanComplianceByMill(data.action_plan_compliance_by_mill);
            }
        }, [data]);

        return {
            loading,
            actionPlanComplianceByMill,
            apolloQueryError: error,
        };
    },
    GetAPComplianceByResponsible: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getAPComplianceByResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.INPUT_DATE),
            },
        });

        const [apComplianceByResponsible, setApComplianceByResponsible] = useState<IAPComplianceByResponsible[]>([]);

        useEffect(() => {
            if (
                data?.action_plan_compliance_totals_by_responsible &&
                Array.isArray(data.action_plan_compliance_totals_by_responsible)
            ) {
                setApComplianceByResponsible(data.action_plan_compliance_totals_by_responsible);
            }
        }, [data]);

        return {
            loading,
            apComplianceByResponsible,
            apolloQueryError: error,
        };
    },
    GetAPAvgPerUser: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getAvgActionPlansPerUserResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.MAX_DATE),
            },
        });

        const [apAvgPerUser, setApAvgPerUser] = useState<IAPAvgPerUser[]>([]);

        useEffect(() => {
            if (data?.avg_action_plans_per_user_by_date && Array.isArray(data.avg_action_plans_per_user_by_date)) {
                setApAvgPerUser(data.avg_action_plans_per_user_by_date);
            }
        }, [data]);

        return {
            loading,
            apAvgPerUser,
            apolloQueryError: error,
        };
    },
    GetRcaMethodologyUsedByUser: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRcaMethodologyUsedByUserResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: buildComplianceArgs(filters, ComplianceArgsDateKey.MAX_DATE),
            },
        });

        const [rcaMethodologyUsedByUser, setRcaMethodologyUsedByUser] = useState<IRcaMethodologyUsedByUser[]>([]);

        useEffect(() => {
            if (data?.rca_methodology_used_by_user && Array.isArray(data.rca_methodology_used_by_user)) {
                setRcaMethodologyUsedByUser(data.rca_methodology_used_by_user);
            }
        }, [data]);

        return {
            loading,
            rcaMethodologyUsedByUser,
            apolloQueryError: error,
        };
    },
    GetRCADisplayNumbers: (rcaIds: number[]) => {
        const { loading, error, data } = useQuery(getRCADisplayNumbers, {
            fetchPolicy: "cache-first",
            variables: {
                rca_ids: rcaIds,
            },
            skip: !rcaIds || rcaIds.length === 0,
        });

        const [rcaDisplayNumbers, setRcaDisplayNumbers] = useState<IRCADisplayNumber[]>([]);

        useEffect(() => {
            if (data?.rca && Array.isArray(data.rca)) {
                setRcaDisplayNumbers(data.rca);
            }
        }, [data]);

        return {
            loading,
            rcaDisplayNumbers,
            apolloQueryError: error,
        };
    },
};
